/**
 * @file useProductsRegistry.ts
 * @description Данные реестра товаров (phase19.31, зона «Товары», макет (а)).
 *
 * Сервер отдаёт товары магазина одним списком (артикулы фасовок —
 * `skuCodes` всегда, контракт §20; `withStock=1` добавляет остаток, W9),
 * поэтому отбор, счётчики пилюль и
 * страницы считаются на месте (`selectProducts`). Состояние — в адресе.
 *
 * Перечитывание тихое: прежний список остаётся на экране, пока идёт новый
 * запрос; ошибка при показанном списке его не стирает (разбор B §3).
 * Остатки — отдельным признаком: если сервер не вернул сводку, колонка
 * «Остаток» показывает «—», а над списком — пояснение.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { catalogCategories, catalogProducts, type CatalogCategory, type CatalogProduct } from "../../../lib/api-catalog";
import { ApiError } from "../../../lib/api-client";
import {
  parseProductsQuery,
  patchProductsQuery,
  productsPage,
  resetProductsFilters,
  selectProducts,
  writeProductsQuery,
  type ProductsRegistryQuery,
} from "../../../lib/catalog-registry-query";
import { productSiteState } from "../../../lib/catalog-status";

export const PRODUCTS_ERROR_TEXT = {
  empty: "Не удалось загрузить товары. Проверьте связь и попробуйте ещё раз",
  stale: "Не удалось обновить список, данные могут быть устаревшими",
} as const;

export function useProductsRegistry() {
  const [params, setParams] = useSearchParams();
  const key = writeProductsQuery(parseProductsQuery(params)).toString();
  const query = useMemo(() => parseProductsQuery(new URLSearchParams(key)), [key]);
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const requestId = useRef(0);

  const refresh = useCallback(() => {
    const id = ++requestId.current;
    setLoading(true);
    Promise.all([catalogProducts.list({ withStock: true }), catalogCategories.list()])
      .then(([p, c]) => {
        if (id !== requestId.current) return;
        setProducts(p);
        setCategories(c);
        setFailed(false);
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        if (!(err instanceof ApiError) && import.meta.env.DEV) console.warn("catalogProducts.list", err);
        setFailed(true);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, []);

  useEffect(() => refresh(), [refresh]);

  const setQuery = useCallback(
    (patch: Partial<ProductsRegistryQuery>) =>
      setParams((prev) => writeProductsQuery(patchProductsQuery(parseProductsQuery(prev), patch)), { replace: true }),
    [setParams],
  );
  const resetFilters = useCallback(
    () => setParams((prev) => writeProductsQuery(resetProductsFilters(parseProductsQuery(prev))), { replace: true }),
    [setParams],
  );

  const selection = useMemo(() => selectProducts(products, categories, query), [products, categories, query]);
  const page = productsPage(selection.rows, query.page, query.pageSize);
  const onSite = useMemo(() => products.filter((p) => productSiteState(p) === "published").length, [products]);
  // Сводка остатков не пришла ни у одного товара — учёт недоступен (разбор B §3).
  const stockMissing = products.length > 0 && products.every((p) => p.stockTracked === undefined);

  return {
    query,
    setQuery,
    resetFilters,
    products,
    categories,
    selection,
    page,
    total: products.length,
    onSite,
    stockMissing,
    loading,
    error: failed ? (products.length > 0 ? PRODUCTS_ERROR_TEXT.stale : PRODUCTS_ERROR_TEXT.empty) : null,
    refresh,
  };
}

export type ProductsRegistry = ReturnType<typeof useProductsRegistry>;
